import { Timeline, Tag, Typography, Empty } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { CompensationStep, CorrectionStatus } from '@shared/types';

const { Text } = Typography;

export interface CorrectionTimelineProps {
  steps: CompensationStep[];
  status: CorrectionStatus;
}

const stepIcons: Record<CompensationStep['status'], React.ReactNode> = {
  pending: <ClockCircleOutlined />,
  completed: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
};

const stepColors: Record<CompensationStep['status'], string> = {
  pending: 'gray',
  completed: 'green',
  failed: 'red',
};

const statusConfig: Record<CorrectionStatus, { color: string; label: string }> = {
  pending_approval: { color: 'orange', label: 'Pending Approval' },
  approved: { color: 'blue', label: 'Approved' },
  executing: { color: 'processing', label: 'Executing' },
  completed: { color: 'success', label: 'Completed' },
  failed: { color: 'error', label: 'Failed' },
  rejected: { color: 'default', label: 'Rejected' },
};

export function CorrectionStatusTag({ status }: { status: CorrectionStatus }) {
  const config = statusConfig[status];
  const icon = status === 'executing' ? <SyncOutlined spin /> : undefined;
  return <Tag color={config.color} icon={icon}>{config.label}</Tag>;
}

export function CorrectionTimeline({ steps, status }: CorrectionTimelineProps) {
  if (!steps.length) {
    return <Empty description="No compensation steps" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const isExecuting = status === 'executing';

  return (
    <Timeline
      items={steps.map((step, idx) => {
        const isCurrentStep = isExecuting && step.status === 'pending' && (idx === 0 || steps[idx - 1].status === 'completed');

        return {
          color: isCurrentStep ? 'blue' : stepColors[step.status],
          dot: isCurrentStep ? <LoadingOutlined /> : stepIcons[step.status],
          children: (
            <div>
              <div style={{ marginBottom: 4 }}>
                <Text strong>{step.module}</Text>
                <Text type="secondary"> / {step.action}</Text>
                {step.entityId && <Text type="secondary"> #{step.entityId}</Text>}
              </div>
              {step.completedAt && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Completed: {new Date(step.completedAt).toLocaleString()}
                </Text>
              )}
              {step.error && (
                <div style={{ marginTop: 4 }}>
                  <Text type="danger" style={{ fontSize: 12 }}>{step.error}</Text>
                </div>
              )}
            </div>
          ),
        };
      })}
    />
  );
}
